import {
  Presentation, BookOpen, TrendingUp, Scale, Gauge,
  ShieldCheck, Landmark, ScrollText, Receipt, PieChart, KeyRound,
} from "lucide-react";

export const docSlug = (title) => title.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const BUSINESS_DOCS = [
  {
    n: "01",
    Icon: Presentation,
    title: "Pitch Deck",
    what: "A short, visual presentation that tells your business story — the problem you solve, your customers, your numbers, your team, and what you're asking for.",
    funders: "It's the first thing they open. In ten minutes, it tells them whether your business fits what they fund. A confused or bloated deck signals a confused business; a sharp one earns you the meeting.",
    you: "Building a deck forces you to answer hard questions simply. If you can't explain your business clearly on a slide, you'll struggle to explain it to a customer, a partner, or an employee either. Clarity here sharpens everything else.",
  },
  {
    n: "02",
    Icon: TrendingUp,
    title: "Financial Model",
    what: "A forward-looking spreadsheet showing where your business is going — projected revenue, costs, and cash flow for the next three to five years, built on clear assumptions.",
    funders: "Funders invest in the future, not the past. The model shows them how big the business can get, when it becomes profitable, and how their money will be used to get there. They will test your assumptions — a well-built model shows you've thought them through.",
    you: "Your model is your business plan in numbers. It tells you how much capital you actually need (many founders ask for too little and run out), when cash will get tight, and what has to be true for your plans to work. It turns hope into a roadmap.",
  },
  {
    n: "03",
    Icon: BookOpen,
    title: "Management Accounts",
    what: "Up-to-date monthly or quarterly financial records — your sales, costs, profit, and cash position — prepared consistently and reconciled properly.",
    funders: "This is where trust is won or lost. Audited statements tell funders about last year; management accounts tell them what's happening right now. Clean, current accounts say \"this founder knows their numbers.\" Gaps and guesswork say the opposite — and nothing closes a funder conversation faster.",
    you: "You can't manage what you can't see. Management accounts show you which products make money, where cash leaks, and whether you can afford that next hire — while there's still time to act. Many founders discover problems (and opportunities) they never knew existed the first time they see their numbers properly laid out.",
  },
  {
    n: "04",
    Icon: ShieldCheck,
    title: "Audited Accounts",
    what: "Your annual financial statements, independently examined and signed off by a licensed auditor.",
    funders: "Banks, DFIs and institutional investors won't move without them. An audit is independent proof that your numbers are real — it converts your claims into verified facts and unlocks the bigger, cheaper cheques.",
    you: "The audit discipline catches errors, leakage and even fraud while they're still small. And every year you file audited accounts, you're building a track record that compounds — future raises get faster and cheaper.",
  },
  {
    n: "05",
    Icon: Landmark,
    title: "Bank Statements",
    what: "Six to twelve months of your business bank statements, showing the actual money flowing in and out.",
    funders: "Cash doesn't lie. Statements let funders verify that the revenue in your deck actually lands in your account — and steady inflows can unlock revenue-based and working-capital finance on their own.",
    you: "Running everything through a dedicated business account — instead of mixing it with personal money — is what builds a fundable financial identity. It's the cheapest credibility upgrade available to any SME.",
  },
  {
    n: "06",
    Icon: KeyRound,
    title: "Security Documents",
    what: "Proof of assets a lender can hold as collateral — a land title deed, vehicle logbook, fixed-deposit or cash-cover letter, debenture over company assets, or a personal/corporate guarantee.",
    funders: "Banks and MFIs lend other people's money, so they need a fallback if repayments stop. Clean, unencumbered security lowers their risk, which usually means a bigger facility, a lower rate and faster approval. Unclear ownership or an existing charge on the asset stalls everything.",
    you: "Knowing exactly what you can pledge — and what it's worth — tells you how much debt you can realistically raise before you walk into the bank. Strong cash flows can sometimes replace hard security, so share your bank statements even if you have no title or logbook.",
  },
  {
    n: "07",
    Icon: ScrollText,
    title: "Legal Documents",
    what: "Certificate of incorporation, CR12 or equivalent, articles of association, board resolutions and your key contracts.",
    funders: "Before money moves, lawyers check that the company legally exists, who is authorised to sign, and that no hidden dispute is waiting to surface. Clean paperwork keeps the deal moving; missing paperwork stalls it for months.",
    you: "A clean legal house speeds up everything — bank accounts, tenders, partnerships, licences — not just fundraising. Fixing it under deal pressure is always more expensive than fixing it now.",
  },
  {
    n: "08",
    Icon: Receipt,
    title: "Tax Compliance",
    what: "A current tax compliance certificate (e.g. KRA TCC) and filings that are up to date.",
    funders: "Institutional capital can't flow into a non-compliant business — it's a hard gate, not a preference. A valid certificate signals regulatory hygiene across the whole company.",
    you: "Staying compliant avoids penalties that quietly eat your margins, and it's the entry ticket to government and corporate contracts — often your biggest customers.",
  },
  {
    n: "09",
    Icon: PieChart,
    title: "Cap Table",
    what: "A clear register of who owns what — shares, options, convertible notes and any promises you've made along the way.",
    funders: "An investor is buying a slice of your company, so they need to see the whole pie. Messy or disputed ownership is one of the fastest deal-killers in African venture and PE deals.",
    you: "Your cap table is your ownership story. Keeping it clean protects your stake as you raise round after round — and stops a forgotten handshake agreement from resurfacing at the worst possible moment.",
  },
  {
    n: "10",
    Icon: Scale,
    title: "Business Valuation Report",
    what: "An independent, professional assessment of what your business is worth today, using methods appropriate for African markets and your stage of business.",
    funders: "Every equity investment is a negotiation about value. A credible valuation report gives that negotiation a starting point grounded in evidence, not emotion. It tells the funder you understand what you're selling and at what price.",
    you: "Without a valuation, you're negotiating blind — and founders who guess usually either scare funders off with an inflated number or give away far too much of their company with a low one. Knowing your worth protects your ownership. It's one of the most expensive documents to get wrong.",
  },
  {
    n: "11",
    Icon: Gauge,
    title: "Clinq Capital Readiness Report",
    what: "Clinq's proprietary assessment of how ready your business is for funding — a Capital Readiness Score built from the requirements of over 1,000 real funder mandates, showing exactly where you stand and what to fix.",
    funders: "It does their screening for them. A business arriving with a strong readiness score has already been assessed against what funders actually look for — which means less risk, less back-and-forth, and a faster path to yes.",
    you: "It's your honest mirror. Instead of sending applications into the dark and wondering why the answer was no, you see your gaps before funders do — and close them. You stop guessing what funders want and start knowing. Then, when you approach the market, you approach it ready.",
  },
];

// funder-application upload slot id -> guide card title
export const SLOT_GUIDE_TITLE = {
  pitch_deck: "Pitch Deck", business_profile: "Pitch Deck",
  financial_model: "Financial Model", cashflow_forecast: "Financial Model",
  financials: "Management Accounts", mgmt_accounts: "Management Accounts",
  bank_statements: "Bank Statements",
  cert_incorporation: "Legal Documents", company_registration: "Legal Documents",
  security_docs: "Security Documents",
};

// Clinq Prep tool that builds each document (none = founders gather it themselves)
const PREP_URL = "https://prep.clinq.africa";
export const PREP_FOR_GUIDE = {
  "Pitch Deck": { url: `${PREP_URL}/pitch/new`, verb: "Start building your pitch deck" },
  "Financial Model": { url: `${PREP_URL}/fm/new`, verb: "Start building your financial model" },
  "Management Accounts": { url: `${PREP_URL}/mgmt/new`, verb: "Start preparing your management accounts" },
  "Business Valuation Report": { url: `${PREP_URL}/valuation/new`, verb: "Start your business valuation" },
};

export const guideForSlot = (slotId) => {
  const title = SLOT_GUIDE_TITLE[slotId];
  return title ? BUSINESS_DOCS.find((d) => d.title === title) || null : null;
};
